// tasksModel.js — E2-WS1-T02 (refactor-deepening / F2, tasks/DAG slice).
//
// Deep module owning the INVARIANTS of the code.tasks slice (the Kiro DAG read-model):
// the canonical shape + defaults, the immutable per-task status update, and the
// flags_json parsing. The store's task actions (setCodeTasks / resetCodeTasks /
// markCodeTaskStatus / loadCodeTasksFromSession) were carrying this inline; now they are
// thin delegations, and the shape contract lives — and is tested — in ONE place.
//
// Deletion test (01-DOCTRINE): delete this and the slice-shape defaults + array guards +
// immutable status update + flags_json parse-safety scatter back across the 4 actions.
// The tasks-slice contract is a real invariant that reappears concentrated → DEEP.
//
// PURE: no store, no window, no React. Testable by its interface.

/** The empty tasks slice — the canonical "no DAG" shape. */
export function emptyTasksSlice() {
  return { runId: null, tasks: [], statusByPlanId: {}, criticalPath: [], planDoc: null, approvedAt: null };
}

/**
 * Shape an incoming (possibly malformed) tasks slice into the canonical contract:
 * arrays are guarded, statusByPlanId is copied, missing fields default. §6 of the plan.
 * @param {object|null|undefined} slice
 */
export function buildTasksSlice(slice) {
  return {
    runId: slice?.runId ?? null,
    tasks: Array.isArray(slice?.tasks) ? slice.tasks : [],
    statusByPlanId: { ...(slice?.statusByPlanId || {}) },
    criticalPath: Array.isArray(slice?.criticalPath) ? slice.criticalPath : [],
    planDoc: slice?.planDoc ?? null,
    approvedAt: slice?.approvedAt ?? null,
  };
}

/** Immutable per-task status update — never mutates the prior statusByPlanId. */
export function withTaskStatus(cur, taskId, status) {
  return { ...cur, statusByPlanId: { ...cur.statusByPlanId, [taskId]: status } };
}

/**
 * Pull the persisted tasks snapshot from session.flags_json.tasks (string or object),
 * defending against malformed JSON. Returns the persisted tasks object, or null when
 * there is none (the caller falls back to emptyTasksSlice()).
 */
export function parseSessionTasks(session) {
  let fj = null;
  if (session && session.flags_json) {
    if (typeof session.flags_json === 'string') {
      try { fj = JSON.parse(session.flags_json); } catch { fj = null; }
    } else if (typeof session.flags_json === 'object') {
      fj = session.flags_json;
    }
  }
  return fj && typeof fj.tasks === 'object' ? fj.tasks : null;
}
